import { createFileRoute, redirect } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { MessageSquare, ArrowLeft } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

import { useAuthStore } from '@/stores/authStore';
import { cn } from '@/lib/cn';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/EmptyState';
import { MessageThread } from '@/features/Messages/MessageThread';
import { OrgAvatar } from '@/features/OrgLayout/primitives/OrgAvatar';
import { useTrainerQuestionInbox, useTrainerTopicThread, useSendTopicMessage } from '@/lib/hooks/useTopicMessages';
import type { TopicThreadPreview } from '@/lib/api/topicMessages';

export const Route = createFileRoute('/trainer/questions')({
  beforeLoad: () => {
    if (!useAuthStore.getState().isAuthenticated) throw redirect({ to: '/login' });
  },
  component: TrainerQuestionsPage,
});

type StatusFilter = 'needs_reply' | 'all' | 'answered';

const threadKey = (t: TopicThreadPreview) => `${t.batch_topic_id}-${t.student_id}`;
const needsReply = (t: TopicThreadPreview) => t.last_sender_id === t.student_id;

function relativeTime(raw: string): string {
  if (!raw) return '';
  const d = new Date(raw.replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return '';
  return formatDistanceToNow(d, { addSuffix: true });
}

function TrainerQuestionsPage() {
  const { data: threads, isLoading } = useTrainerQuestionInbox(true);
  const [status, setStatus] = useState<StatusFilter>('needs_reply');
  const [batchId, setBatchId] = useState<string>('all');
  const [selKey, setSelKey] = useState<string | null>(null);

  const all = useMemo(() => threads ?? [], [threads]);
  const needsReplyCount = all.filter(needsReply).length;

  const batches = useMemo(() => {
    const map = new Map<string, string>();
    all.forEach((t) => map.set(t.batch_id, t.batch_name));
    return Array.from(map, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [all]);

  const visible = useMemo(() => {
    return all
      .filter((t) => (batchId === 'all' ? true : t.batch_id === batchId))
      .filter((t) => {
        if (status === 'needs_reply') return needsReply(t);
        if (status === 'answered') return !needsReply(t);
        return true;
      })
      .sort((a, b) => {
        // Needs-reply first, then most-recent activity.
        const aNeeds = needsReply(a) ? 1 : 0;
        const bNeeds = needsReply(b) ? 1 : 0;
        if (aNeeds !== bNeeds) return bNeeds - aNeeds;
        return (b.last_at ?? '').localeCompare(a.last_at ?? '');
      });
  }, [all, batchId, status]);

  const selected = visible.find((t) => threadKey(t) === selKey) ?? null;

  const tabs: { key: StatusFilter; label: string; count?: number }[] = [
    { key: 'needs_reply', label: 'Needs reply', count: needsReplyCount },
    { key: 'all', label: 'All', count: all.length },
    { key: 'answered', label: 'Answered' },
  ];

  return (
    <div className="mx-auto w-full max-w-6xl">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-[#111827]">Questions</h1>
          <p className="mt-1 text-sm text-[#6b7280]">Topic questions from students across your batches.</p>
        </div>
        {batches.length > 1 && (
          <select
            value={batchId}
            onChange={(e) => setBatchId(e.target.value)}
            className="h-9 rounded-lg border border-[#ececec] bg-white px-3 text-sm text-[#111827]"
            aria-label="Filter by batch"
          >
            <option value="all">All batches</option>
            {batches.map((b) => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
        )}
      </div>

      <div className="mt-4 flex gap-1 border-b border-[#ececec]">
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setStatus(t.key)}
            className={cn(
              'inline-flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium transition-colors',
              status === t.key
                ? 'border-[#ff5a1f] text-[#111827]'
                : 'border-transparent text-[#6b7280] hover:text-[#111827]',
            )}
          >
            {t.label}
            {t.count != null && t.count > 0 && (
              <span className={cn(
                'rounded-full px-1.5 py-0.5 text-[11px] font-semibold',
                t.key === 'needs_reply' ? 'bg-[#fff1ec] text-[#ff5a1f]' : 'bg-[#f3f4f6] text-[#6b7280]',
              )}>{t.count}</span>
            )}
          </button>
        ))}
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-[minmax(0,380px)_1fr]">
        {/* Thread list */}
        <div className={cn('min-w-0', selected && 'hidden lg:block')}>
          {isLoading ? (
            <div className="space-y-2">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-16 rounded-xl" />)}</div>
          ) : visible.length === 0 ? (
            <EmptyState
              icon={MessageSquare}
              title={status === 'needs_reply' ? 'All caught up' : 'No questions yet'}
              description={
                status === 'needs_reply'
                  ? 'No questions are waiting on you right now.'
                  : 'When students ask about a topic, their threads appear here.'
              }
            />
          ) : (
            <div className="divide-y divide-[#ececec] overflow-hidden rounded-2xl border border-[#ececec] bg-white">
              {visible.map((t) => {
                const key = threadKey(t);
                const waiting = needsReply(t);
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setSelKey(key)}
                    className={cn(
                      'flex w-full items-start gap-3 px-3 py-3 text-left transition-colors',
                      key === selKey ? 'bg-[#f3f4f6]/60' : 'hover:bg-[#fafafa]/40',
                    )}
                  >
                    <span className="mt-1.5 flex h-2 w-2 shrink-0 items-center justify-center">
                      {waiting && <span className="h-2 w-2 rounded-full bg-[#ff5a1f]" aria-label="Needs reply" />}
                    </span>
                    <OrgAvatar name={t.student_name} size={36} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <p className={cn('truncate text-sm', waiting ? 'font-semibold text-[#111827]' : 'font-medium text-[#111827]')}>
                          {t.student_name}
                        </p>
                        <span className="shrink-0 text-[11px] text-[#6b7280]">{relativeTime(t.last_at)}</span>
                      </div>
                      <p className="truncate text-xs text-[#6b7280]">{t.topic_title}</p>
                      <div className="mt-1 flex items-center gap-2">
                        <span className="truncate rounded-full bg-[#f3f4f6] px-2 py-0.5 text-[10px] font-medium text-[#6b7280]">
                          {t.batch_name}
                        </span>
                        <p className="truncate text-xs text-[#6b7280]">{t.last_body}</p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Conversation */}
        <div className={cn('min-w-0', !selected && 'hidden lg:block')}>
          {selected ? (
            <ThreadPane thread={selected} onBack={() => setSelKey(null)} />
          ) : (
            <div className="hidden h-full min-h-[420px] flex-col items-center justify-center rounded-2xl border border-dashed border-[#ececec] text-center lg:flex">
              <MessageSquare className="h-8 w-8 text-[#6b7280]" />
              <p className="mt-2 text-sm text-[#6b7280]">Select a question to read and reply.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ThreadPane({ thread, onBack }: { thread: TopicThreadPreview; onBack: () => void }) {
  const { data, isLoading } = useTrainerTopicThread(thread.batch_topic_id, thread.student_id, true);
  const send = useSendTopicMessage(['topic-messages', 'trainer', thread.batch_topic_id, thread.student_id]);
  return (
    <div className="rounded-2xl border border-[#ececec] bg-white p-4">
      <div className="flex items-start gap-3 border-b border-[#ececec] pb-3">
        <button
          type="button"
          onClick={onBack}
          className="mt-0.5 inline-flex rounded-lg border border-[#ececec] p-1.5 text-[#6b7280] lg:hidden"
          aria-label="Back to questions"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <OrgAvatar name={thread.student_name} size={40} />
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-[#111827]">{thread.student_name}</p>
          <p className="truncate text-xs text-[#6b7280]">{thread.topic_title} · {thread.batch_name}</p>
        </div>
      </div>
      <MessageThread
        messages={data?.messages ?? []}
        self={data?.self ?? ''}
        loading={isLoading}
        sending={send.isPending}
        onSend={(body) => send.mutate({ batch_topic_id: thread.batch_topic_id, student_id: thread.student_id, body })}
      />
    </div>
  );
}
